// EmployeeAttendanceSummary.jsx
import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchEmployeeSummary,
  updateAttendanceStatus,
  updateAttendancePunches,
} from '../features/attendance/attendanceThunks';
import {
  selectEmployeeSummary,
  selectEmployeeSummaryLoading,
  selectEmployeeSummaryError,
  selectEditLoading,
  selectEditError,
  clearEmployeeSummary,
  clearEditError,
  updateLocalStatus,
} from '../features/attendance/attendanceSlice';
import { canUpdateAttendance } from '../RoleBasedRoute';
import attendanceApi from '../api/attendanceApi';
import '../CSS/EmployeeAttendanceSummary.css';
import Sidebar from './Sidebar';

// ─── Constants ────────────────────────────────────────────────────────────────
const STATUS_OPTIONS = [
  { value: 'PRESENT',       label: 'Present' },
  { value: 'LATE',          label: 'Late' },
  { value: 'ABSENT',        label: 'Absent' },
  { value: 'HALF_DAY',      label: 'Half Day' },
  { value: 'LEAVE',         label: 'Leave' },
  { value: 'MEDICAL_LEAVE', label: 'Medical Leave' },
  { value: 'WEEK_OFF',      label: 'Week Off' },
];

// Extract "HH:MM" from an ISO string, displayed in IST
function isoToTimeInput(isoString) {
  if (!isoString) return '';
  return new Date(isoString).toLocaleTimeString('en-GB', {
    timeZone: 'Asia/Kolkata',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

// Extract "YYYY-MM-DD" from an ISO string, in IST
function isoToDateInput(isoString) {
  if (!isoString) return '';
  return new Date(isoString).toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' }); // "YYYY-MM-DD"
}

// Combine a date string + time string into a proper IST ISO string for the backend
function combineToIso(dateStr, timeStr) {
  if (!dateStr || !timeStr) return null;
  return new Date(`${dateStr}T${timeStr}:00+05:30`).toISOString();
}

// Resolve name from either flat { name } or nested { user: { first_name, last_name } } shape
const resolveEmpName = (emp) => {
  const nested = `${emp.user?.first_name || ''} ${emp.user?.last_name || ''}`.trim();
  return nested || emp.name?.trim() || '—';
};

// ─── Status CSS helper ────────────────────────────────────────────────────────
const getStatusClass = (status) => {
  const map = {
    'PRESENT':       'emp-summary-status-present',
    'LATE':          'emp-summary-status-late',
    'ABSENT':        'emp-summary-status-absent',
    'HALF_DAY':      'emp-summary-status-halfday',
    'LEAVE':         'emp-summary-status-leave',
    'MEDICAL_LEAVE': 'emp-summary-status-medical',
    'WEEK_OFF':      'emp-summary-status-weekoff',
  };
  return map[status] || 'emp-summary-status-default';
};

// ─── Night-shift carry-over badge ─────────────────────────────────────────────
const NightShiftBadge = () => (
  <span
    className="emp-summary-night-shift-badge"
    title="Night shift carry-over — record is dated the previous day; employee was still in office"
  >
    🌙 Night
  </span>
);

// ─── Skeleton Components ──────────────────────────────────────────────────────
const SkeletonInfoCard = () => (
  <div className="emp-summary-employee-info-card">
    <div className="emp-summary-info-header">
      <div className="emp-summary-skeleton-title"></div>
      <div className="emp-summary-skeleton-code"></div>
    </div>
    <div className="emp-summary-info-details">
      {[...Array(3)].map((_, i) => (
        <div key={i} className="emp-summary-detail-item">
          <div className="emp-summary-skeleton-label"></div>
          <div className="emp-summary-skeleton-value"></div>
        </div>
      ))}
    </div>
  </div>
);

const SkeletonSummaryCard = () => (
  <div className="emp-summary-summary-card">
    <div className="emp-summary-card-content">
      <div className="emp-summary-skeleton-number"></div>
      <div className="emp-summary-skeleton-text"></div>
    </div>
  </div>
);

// colCount: 7 with actions column, 6 without
const SkeletonTableRow = ({ colCount = 7 }) => (
  <tr className="emp-summary-skeleton-row">
    {[...Array(colCount)].map((_, i) => (
      <td key={i}><div className="emp-summary-skeleton-cell"></div></td>
    ))}
  </tr>
);

// ─── Edit Modal ───────────────────────────────────────────────────────────────
// Only rendered for ADMIN and HR users.
const EditAttendanceModal = ({ record, onClose, onSave, editLoading, editError }) => {
  const [activeTab,      setActiveTab]      = useState('status');
  const [selectedStatus, setSelectedStatus] = useState(record.status || 'PRESENT');
  const [statusRemarks,  setStatusRemarks]  = useState('');
  // Date + time inputs — date defaults to record.date, but admin can change it.
  // This cleanly handles night-shift punch-outs that fall on the next calendar day.
  const [punchInDate,    setPunchInDate]    = useState(isoToDateInput(record.punch_in)  || record.date);
  const [punchInTime,    setPunchInTime]    = useState(isoToTimeInput(record.punch_in));
  const [punchOutDate,   setPunchOutDate]   = useState(isoToDateInput(record.punch_out) || record.date);
  const [punchOutTime,   setPunchOutTime]   = useState(isoToTimeInput(record.punch_out));
  const [punchRemarks,   setPunchRemarks]   = useState('');

  // If punch-out time is typed earlier than punch-in time (and same date), auto-advance date by 1
  const handlePunchOutTimeChange = (val) => {
    setPunchOutTime(val);
    if (punchInTime && val && punchOutDate === punchInDate && val < punchInTime) {
      const nextDay = new Date(new Date(`${punchInDate}T00:00:00+05:30`).getTime() + 86400000)
        .toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
      setPunchOutDate(nextDay);
    }
  };

  const handleSave = () => {
    if (activeTab === 'status') {
      onSave({ type: 'status', recordId: record.id, status: selectedStatus, remarks: statusRemarks, oldStatus: record.status });
    } else {
      const punch_in  = punchInTime  ? combineToIso(punchInDate,  punchInTime)  : null;
      const punch_out = punchOutTime ? combineToIso(punchOutDate, punchOutTime) : null;
      if (!punch_in && !punch_out) return;
      onSave({ type: 'punches', recordId: record.id, punch_in, punch_out, remarks: punchRemarks });
    }
  };

  const handleBackdrop = (e) => { if (e.target === e.currentTarget) onClose(); };

  const recordDateLabel = record.date
    ? new Date(record.date + 'T00:00:00+05:30').toLocaleDateString('en-IN', {
        weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
        timeZone: 'Asia/Kolkata',
      })
    : '';

  return (
    <div className="emp-modal-backdrop" onClick={handleBackdrop}>
      <div className="emp-modal">

        {/* Header */}
        <div className="emp-modal-header">
          <div>
            <h2 className="emp-modal-title">Edit Attendance</h2>
            <p className="emp-modal-subtitle">
              <span className="emp-modal-date">{recordDateLabel}</span>
              {record.is_night_shift_carry && (
                <span className="emp-modal-night-tag"> · 🌙 Night shift carry-over</span>
              )}
            </p>
          </div>
          <button className="emp-modal-close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        {/* Tabs */}
        <div className="emp-modal-tabs">
          <button
            className={`emp-modal-tab ${activeTab === 'status'  ? 'emp-modal-tab-active' : ''}`}
            onClick={() => setActiveTab('status')}
          >
            ⬤ Status
          </button>
          <button
            className={`emp-modal-tab ${activeTab === 'punches' ? 'emp-modal-tab-active' : ''}`}
            onClick={() => setActiveTab('punches')}
          >
            🕐 Punch Times
          </button>
        </div>

        {/* Body */}
        <div className="emp-modal-body">
          {activeTab === 'status' ? (
            <>
              <div className="emp-modal-current">
                <span className="emp-modal-label">Current Status</span>
                <span className={`emp-summary-status-badge emp-modal-current-badge ${getStatusClass(record.status)}`}>
                  {record.status.replace(/_/g, ' ')}
                </span>
              </div>
              <div className="emp-modal-field">
                <label className="emp-modal-label">New Status</label>
                <div className="emp-modal-status-grid">
                  {STATUS_OPTIONS.map(opt => (
                    <button
                      key={opt.value}
                      className={`emp-modal-status-btn emp-modal-status-${opt.value.toLowerCase()} ${selectedStatus === opt.value ? 'emp-modal-status-selected' : ''}`}
                      onClick={() => setSelectedStatus(opt.value)}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="emp-modal-field">
                <label className="emp-modal-label">
                  Remarks <span className="emp-modal-optional">(optional)</span>
                </label>
                <textarea
                  className="emp-modal-textarea"
                  placeholder="Reason for status change..."
                  value={statusRemarks}
                  onChange={e => setStatusRemarks(e.target.value)}
                  rows={3}
                />
              </div>
            </>
          ) : (
            <>
              <div className="emp-modal-punch-current">
                <div className="emp-modal-punch-info">
                  <span className="emp-modal-label">Current Punch In</span>
                  <span className="emp-modal-punch-value">{isoToTimeInput(record.punch_in) || '—'}</span>
                </div>
                <div className="emp-modal-punch-arrow">→</div>
                <div className="emp-modal-punch-info">
                  <span className="emp-modal-label">Current Punch Out</span>
                  <span className="emp-modal-punch-value">{isoToTimeInput(record.punch_out) || '—'}</span>
                </div>
              </div>
              {record.is_night_shift_carry && (
                <div className="emp-modal-night-note">
                  🌙 Night shift carry-over — attendance date is{' '}
                  <strong>{record.date}</strong>. Punch times are adjusted against that date.
                </div>
              )}
              <p className="emp-modal-punch-note">
                ℹ️ Status will be automatically recalculated from the new times.
              </p>
              {/* Punch-in: date + time */}
              <div className="emp-modal-field">
                <label className="emp-modal-label">New Punch In</label>
                <div className="emp-modal-datetime-row">
                  <input
                    type="date"
                    className="emp-modal-date-input"
                    value={punchInDate}
                    onChange={e => setPunchInDate(e.target.value)}
                  />
                  <input
                    type="time"
                    className="emp-modal-time-input"
                    value={punchInTime}
                    onChange={e => setPunchInTime(e.target.value)}
                  />
                </div>
              </div>

              {/* Punch-out: date + time — date may differ for night shift */}
              <div className="emp-modal-field">
                <label className="emp-modal-label">New Punch Out</label>
                <div className="emp-modal-datetime-row">
                  <input
                    type="date"
                    className="emp-modal-date-input"
                    value={punchOutDate}
                    onChange={e => setPunchOutDate(e.target.value)}
                  />
                  <input
                    type="time"
                    className="emp-modal-time-input"
                    value={punchOutTime}
                    onChange={e => handlePunchOutTimeChange(e.target.value)}
                  />
                </div>
                {punchOutDate !== punchInDate && punchOutTime && (
                  <div className="emp-modal-nextday-badge">
                    🌙 Punch-out is on a different day than punch-in
                    {punchOutDate > punchInDate ? ' (+next day)' : ' (⚠ earlier date)'}
                  </div>
                )}
              </div>
              <div className="emp-modal-field">
                <label className="emp-modal-label">
                  Remarks <span className="emp-modal-optional">(optional)</span>
                </label>
                <textarea
                  className="emp-modal-textarea"
                  placeholder="Reason for correction (e.g. biometric reader error)..."
                  value={punchRemarks}
                  onChange={e => setPunchRemarks(e.target.value)}
                  rows={3}
                />
              </div>
            </>
          )}
          {editError && <div className="emp-modal-error">⚠ {editError}</div>}
        </div>

        {/* Footer */}
        <div className="emp-modal-footer">
          <button className="emp-modal-btn-cancel" onClick={onClose} disabled={editLoading}>Cancel</button>
          <button
            className="emp-modal-btn-save"
            onClick={handleSave}
            disabled={editLoading || (activeTab === 'punches' && !punchInTime && !punchOutTime)}
          >
            {editLoading ? (
              <span className="emp-modal-saving"><span className="emp-modal-spinner"></span> Saving...</span>
            ) : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const EmployeeAttendanceSummary = () => {
  const dispatch        = useDispatch();
  const user            = useSelector((state) => state.auth.user);
  const employeeSummary = useSelector(selectEmployeeSummary);
  const loading         = useSelector(selectEmployeeSummaryLoading);
  const error           = useSelector(selectEmployeeSummaryError);
  const editLoading     = useSelector(selectEditLoading);
  const editError       = useSelector(selectEditError);

  const [selectedEmployeeId, setSelectedEmployeeId] = useState('');
  const [selectedMonth,      setSelectedMonth]      = useState(new Date().getMonth() + 1);
  const [selectedYear,       setSelectedYear]       = useState(new Date().getFullYear());
  const [employees,          setEmployees]          = useState([]);
  const [loadingEmployees,   setLoadingEmployees]   = useState(true);
  const [searchTerm,         setSearchTerm]         = useState('');
  const [editRecord,         setEditRecord]         = useState(null);
  const [successMsg,         setSuccessMsg]         = useState('');

  // PERMISSION: only ADMIN and HR can update attendance status / punch times.
  // Team Managers (rank 5–8) can VIEW records but the Edit button is hidden.
  const canEdit = canUpdateAttendance(user);

  // Number of table columns — shrinks by 1 when Actions is hidden
  const tableColCount = canEdit ? 7 : 6;

  // Fetch employee list for the dropdown.
  // The backend /admin/summary/ endpoint enforces rank + dept checks per request,
  // so if this user picks an employee they can't see they'll get a 403 error.
  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const response = await attendanceApi.getAllEmployees();
        setEmployees(response.data || []);
      } catch (err) {
        console.error('Error fetching employees:', err);
      } finally {
        setLoadingEmployees(false);
      }
    };
    fetchEmployees();
  }, []);

  useEffect(() => {
    if (selectedEmployeeId) {
      dispatch(fetchEmployeeSummary({
        employeeId: selectedEmployeeId,
        month: selectedMonth,
        year: selectedYear,
      }));
    } else {
      dispatch(clearEmployeeSummary());
    }
  }, [dispatch, selectedEmployeeId, selectedMonth, selectedYear]);

  const years        = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i);
  const getMonthName = (m) => new Date(2000, m - 1, 1).toLocaleDateString('en-US', { month: 'long' });
  // Always display in IST (backend stores IST)
  const formatTime   = (dt) => {
    if (!dt) return '--:--';
    return new Date(dt).toLocaleTimeString('en-IN', {
      hour: '2-digit', minute: '2-digit', hour12: true,
      timeZone: 'Asia/Kolkata',
    });
  };

  const filteredEmployees = employees.filter(emp => {
    const fullName = resolveEmpName(emp);
    return (
      fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      emp.id.toString().includes(searchTerm)
    );
  });

  const openEdit = useCallback((record) => {
    dispatch(clearEditError());
    setSuccessMsg('');
    setEditRecord(record);
  }, [dispatch]);

  const closeEdit = () => {
    setEditRecord(null);
    dispatch(clearEditError());
  };

  const handleSave = useCallback(async (payload) => {
    let result;
    if (payload.type === 'status') {
      dispatch(updateLocalStatus({ recordId: payload.recordId, newStatus: payload.status }));
      result = await dispatch(updateAttendanceStatus({
        recordId:  payload.recordId,
        status:    payload.status,
        remarks:   payload.remarks,
        oldStatus: payload.oldStatus,
      }));
    } else {
      result = await dispatch(updateAttendancePunches({
        recordId:  payload.recordId,
        punch_in:  payload.punch_in,
        punch_out: payload.punch_out,
        remarks:   payload.remarks,
      }));
    }
    if (!result.error) {
      setSuccessMsg(
        payload.type === 'status'
          ? `Status updated to ${payload.status.replace(/_/g, ' ')}`
          : 'Punch times updated successfully'
      );
      setEditRecord(null);
      dispatch(fetchEmployeeSummary({
        employeeId: selectedEmployeeId,
        month: selectedMonth,
        year: selectedYear,
      }));
    }
  }, [dispatch, selectedEmployeeId, selectedMonth, selectedYear]);

  return (
    <div className="index-box">
      <Sidebar />
      <div className="emp-summary-employee-attendance-summary">

        {/* Header */}
        <div className="emp-summary-summary-header">
          <div className="emp-summary-header-left">
            <h1>Employee Attendance Summary</h1>
            <p className="emp-summary-subtitle">
              {canEdit
                ? 'View and manage employee attendance records'
                : 'View attendance records for employees in your department'}
            </p>
          </div>
          {successMsg && <div className="emp-summary-success-toast">{successMsg}</div>}
        </div>

        {/* View-only banner for non-edit roles */}
        {!canEdit && (
          <div className="emp-summary-viewonly-banner">
      
          </div>
        )}

        {/* Selection Panel */}
        <div className="emp-summary-selection-panel">
          <div className="emp-summary-employee-selector">
            <label htmlFor="employee-search">Select Employee</label>
            <div className="emp-summary-employee-search-container">
              <input
                id="employee-search"
                type="text"
                placeholder="Search by name or ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="emp-summary-employee-search"
              />
              <select
                value={selectedEmployeeId}
                onChange={(e) => {
                  setSelectedEmployeeId(e.target.value);
                  setSearchTerm('');
                  setSuccessMsg('');
                }}
                className="emp-summary-employee-select"
                disabled={loadingEmployees}
              >
                <option value="">-- Select an employee --</option>
                {filteredEmployees.map(emp => (
                  <option key={emp.id} value={emp.id}>
                    {emp.id} - {resolveEmpName(emp)} ({emp.department || 'N/A'})
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="emp-summary-date-filters">
            <div className="emp-summary-filter-group">
              <label htmlFor="month-select">Month</label>
              <select
                id="month-select"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(Number(e.target.value))}
                className="emp-summary-month-select"
                disabled={!selectedEmployeeId}
              >
                {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                  <option key={m} value={m}>{getMonthName(m)}</option>
                ))}
              </select>
            </div>
            <div className="emp-summary-filter-group">
              <label htmlFor="year-select">Year</label>
              <select
                id="year-select"
                value={selectedYear}
                onChange={(e) => setSelectedYear(Number(e.target.value))}
                className="emp-summary-year-select"
                disabled={!selectedEmployeeId}
              >
                {years.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
          </div>
        </div>

        {/* Content */}
        {!selectedEmployeeId ? (
          <div className="emp-summary-empty-state">
            <div className="emp-summary-empty-icon">👤</div>
            <h2>No Employee Selected</h2>
            <p>Please select an employee from the dropdown above to view their attendance summary</p>
          </div>

        ) : loading ? (
          <>
            <SkeletonInfoCard />
            <div className="emp-summary-summary-cards">
              {[...Array(6)].map((_, i) => <SkeletonSummaryCard key={i} />)}
            </div>
            <div className="emp-summary-records-section">
              <div className="emp-summary-section-header">
                <div className="emp-summary-skeleton-section-title"></div>
                <div className="emp-summary-skeleton-record-count"></div>
              </div>
              <div className="emp-summary-table-container">
                <table className="emp-summary-attendance-table">
                  <thead>
                    <tr>
                      <th>Date</th><th>Day</th><th>Check In</th>
                      <th>Check Out</th><th>Work Hours</th><th>Status</th>
                      {canEdit && <th>Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {[...Array(5)].map((_, i) => (
                      <SkeletonTableRow key={i} colCount={tableColCount} />
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>

        ) : error ? (
          <div className="emp-summary-error-container">
            <p className="emp-summary-error-message">{error}</p>
            <button
              onClick={() => dispatch(fetchEmployeeSummary({
                employeeId: selectedEmployeeId,
                month: selectedMonth,
                year: selectedYear,
              }))}
              className="emp-summary-btn-retry"
            >
              Retry
            </button>
          </div>

        ) : (
          <>
            {/* Employee Info Card */}
            <div className="emp-summary-employee-info-card">
              <div className="emp-summary-info-header">
                <h2>{employeeSummary.employeeName}</h2>
                <span className="emp-summary-employee-code">ID: {employeeSummary.employeeCode}</span>
              </div>
              <div className="emp-summary-info-details">
                <div className="emp-summary-detail-item">
                  <span className="emp-summary-label">Department:</span>
                  <span className="emp-summary-value">{employeeSummary.department || 'N/A'}</span>
                </div>
                <div className="emp-summary-detail-item">
                  <span className="emp-summary-label">Role:</span>
                  <span className="emp-summary-value">{employeeSummary.role || 'N/A'}</span>
                </div>
                <div className="emp-summary-detail-item">
                  <span className="emp-summary-label">Period:</span>
                  <span className="emp-summary-value">{getMonthName(selectedMonth)} {selectedYear}</span>
                </div>
              </div>
            </div>

            {/* Summary Cards */}
            <div className="emp-summary-summary-cards">
              <div className="emp-summary-summary-card emp-summary-card-present">
                <div className="emp-summary-card-content">
                  <h3>{employeeSummary.summary.present}</h3><p>Present</p>
                </div>
              </div>
              <div className="emp-summary-summary-card emp-summary-card-late">
                <div className="emp-summary-card-content">
                  <h3>{employeeSummary.summary.late}</h3><p>Late</p>
                </div>
              </div>
              <div className="emp-summary-summary-card emp-summary-card-absent">
                <div className="emp-summary-card-content">
                  <h3>{employeeSummary.summary.absent}</h3><p>Absent</p>
                </div>
              </div>
              <div className="emp-summary-summary-card emp-summary-card-halfday">
                <div className="emp-summary-card-content">
                  <h3>{employeeSummary.summary.halfDay}</h3><p>Half Day</p>
                </div>
              </div>
              <div className="emp-summary-summary-card emp-summary-card-leave">
                <div className="emp-summary-card-content">
                  <h3>{employeeSummary.summary.onLeave}</h3><p>On Leave</p>
                </div>
              </div>
              <div className="emp-summary-summary-card emp-summary-card-hours">
                <div className="emp-summary-card-content">
                  <h3>{employeeSummary.summary.avgWorkHours.toFixed(1)}</h3><p>Avg Hours/Day</p>
                </div>
              </div>
            </div>

            {/* Records Table */}
            <div className="emp-summary-records-section">
              <div className="emp-summary-section-header">
                <h3>Attendance Records</h3>
                <span className="emp-summary-record-count">{employeeSummary.count} records</span>
              </div>
              <div className="emp-summary-table-container">
                <table className="emp-summary-attendance-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Day</th>
                      <th>Check In</th>
                      <th>Check Out</th>
                      <th>Work Hours</th>
                      <th>Status</th>
                      {/* Actions column only for ADMIN/HR */}
                      {canEdit && <th>Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {employeeSummary.records.length > 0 ? (
                      employeeSummary.records.map((record) => (
                        <tr
                          key={record.id}
                          className={record.is_night_shift_carry ? 'emp-summary-night-shift-row' : ''}
                        >
                          <td>{new Date(record.date + 'T00:00:00+05:30').toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' })}</td>
                          <td>{new Date(record.date + 'T00:00:00+05:30').toLocaleDateString('en-IN', { weekday: 'short', timeZone: 'Asia/Kolkata' })}</td>
                          <td>{formatTime(record.punch_in)}</td>
                          <td>{formatTime(record.punch_out)}</td>
                          <td>{record.work_hours ? `${record.work_hours.toFixed(2)} hrs` : '--'}</td>
                          <td>
                            <span className={`emp-summary-status-badge ${getStatusClass(record.status)}`}>
                              {record.status.replace(/_/g, ' ')}
                              {record.is_night_shift_carry && <NightShiftBadge />}
                            </span>
                          </td>
                          {canEdit && (
                            <td>
                              <button
                                className="emp-summary-btn-edit"
                                onClick={() => openEdit(record)}
                                title="Edit this record"
                              >
                                ✏ Edit
                              </button>
                            </td>
                          )}
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={tableColCount} className="emp-summary-no-data">
                          No attendance records for this period
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Stats Footer */}
            <div className="emp-summary-stats-footer">
              <div className="emp-summary-stat-item">
                <span className="emp-summary-stat-label">Total Records:</span>
                <span className="emp-summary-stat-value">{employeeSummary.summary.totalRecords}</span>
              </div>
              <div className="emp-summary-stat-item">
                <span className="emp-summary-stat-label">Total Work Hours:</span>
                <span className="emp-summary-stat-value">
                  {employeeSummary.summary.totalWorkHours.toFixed(2)} hrs
                </span>
              </div>
              <div className="emp-summary-stat-item">
                <span className="emp-summary-stat-label">Attendance Rate:</span>
                <span className="emp-summary-stat-value">
                  {employeeSummary.summary.totalRecords > 0
                    ? ((employeeSummary.summary.present / employeeSummary.summary.totalRecords) * 100).toFixed(1)
                    : 0}%
                </span>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Edit Modal — only mounted when user has edit rights */}
      {editRecord && canEdit && (
        <EditAttendanceModal
          record={editRecord}
          onClose={closeEdit}
          onSave={handleSave}
          editLoading={editLoading}
          editError={editError}
        />
      )}
    </div>
  );
};

export default EmployeeAttendanceSummary;